import { newId } from "../ids.js";
import type { RoleName } from "./roles.js";
import { DEFAULT_CHROMA_BOOST, type SchemeVariant, type ThemeConfig, type ThemeMode } from "./types.js";

/** Per-mode overrides of generated chrome roles a preset wants to force, so an
 *  aesthetic can pin its signature surface/ink/accent even where Material's
 *  generation wouldn't reach (e.g. cyberpunk's blue-black ground). */
type PresetRoleOverrides = { light?: Partial<Record<RoleName, string>>; dark?: Partial<Record<RoleName, string>> };

/**
 * A theme is a curated AESTHETIC, not a single seed hue. Each preset ships its
 * own hand-picked entity palette (the topic/calendar chip colors — a real
 * RANGE, not one rotated hue), a chrome mood (source + variant + a few targeted
 * role overrides), and the light/dark mode it's designed for (applied on pick).
 */
export interface ThemePreset {
  id: string;
  name: string;
  /** One-line vibe shown in the picker. */
  description: string;
  /** Chrome seed (light). */
  source: string;
  /** Optional independent chrome seed for dark mode. */
  sourceDark?: string;
  variant: SchemeVariant;
  /** The aesthetic's natural mode — set as the app appearance when picked. */
  defaultMode: ThemeMode;
  /** Curated categorical chip colors (10+ distinct, harmonious hues). */
  entityPalette: string[];
  /** Optional chrome role overrides to pin the signature look. */
  roleOverrides?: PresetRoleOverrides;
}

export const DEFAULT_PRESET_ID = "default";
export const DEFAULT_BLOCK_TINT_DIFF = 0.22;

const LEGACY_DEFAULT_ENTITY_PALETTE = [
  "#4f46e5", "#0d9488", "#d97706", "#e11d48", "#16a34a",
  "#7c3aed", "#0891b2", "#ea580c", "#db2777", "#65a30d",
];

const LEGACY_ART_NOUVEAU_ENTITY_PALETTE = [
  "#1f7a7a", "#c79a3b", "#8a9a6b", "#6b3b5e", "#b1623b",
  "#b08d2f", "#bd8a8a", "#5d6b3e", "#5a7088", "#7e3b40",
];

const LEGACY_SCANDINAVIAN_ENTITY_PALETTE = [
  "#7e98ad", "#93a98c", "#b8987a", "#cf9089", "#6b7178",
  "#c7a45e", "#b3795f", "#6f8f8c", "#9a8a9e", "#a98f4f",
];

const LEGACY_DARK_ACADEMIA_ENTITY_PALETTE = [
  "#a8413f", "#5a7d63", "#c08a2d", "#3f5d8a", "#7a4f6e",
  "#b79b6e", "#8a8f6b", "#c2693f", "#5a8a94", "#9a4a50",
];

const LEGACY_WABI_SABI_ENTITY_PALETTE = [
  "#1f4e8c", "#c66b27", "#7a9033", "#4a4a4a", "#cf7f8a",
  "#a9785a", "#6b8e6b", "#b03b3b", "#3a5680", "#b8902f",
];

const LEGACY_GOTHIC_ENTITY_PALETTE = [
  "#b02437", "#8b5cb0", "#7a818c", "#b8902f", "#c2bba8",
  "#2f6b6b", "#7a2a30", "#5e5a82", "#4f6650", "#9a958a",
];

const LEGACY_BAROQUE_ENTITY_PALETTE = [
  "#d4af37", "#b8323a", "#1f6b48", "#2f3e8f", "#7e3590",
  "#cdb89a", "#a87d2f", "#8a2230", "#1f6b6b", "#bf6a30",
];

const LEGACY_BAUHAUS_ENTITY_PALETTE = [
  "#c8302a", "#e0a400", "#1e3878", "#3a3a3a", "#e06b1f",
  "#1f7a7a", "#2f7d3f", "#b0306a", "#2d52a8", "#8a8a8a",
];

const LEGACY_CYBERPUNK_ENTITY_PALETTE = [
  "#ff2bd6", "#0abdc6", "#b026ff", "#5ef38c", "#ff3d81",
  "#2d6cff", "#ffb300", "#ff5e00", "#a6ff2b", "#7a5cff",
];

const LEGACY_COTTAGECORE_ENTITY_PALETTE = [
  "#8aa05a", "#c98a86", "#d9a93f", "#c2693f", "#6f97a8",
  "#9b8fc0", "#5f7350", "#e0a07a", "#7d5267", "#b39a4a",
];

/**
 * Ten curated aesthetics. Palettes researched from the corresponding art/design
 * movements (see the plan's Sources), tuned to mid tone/usable saturation so the
 * block wash reads on both light and dark surfaces.
 */
export const THEME_PRESETS: readonly ThemePreset[] = [
  {
    id: "default",
    name: "Modern",
    description: "Clean and balanced",
    // Warm coral seed drawn from the palette's own lead hue — the old indigo
    // chrome shared no color with the warm entity palette. `primary` is pinned
    // to a terracotta (vibrant would otherwise collapse the coral seed to a
    // near-pure red that collides with the destructive red).
    source: "#ED6154",
    variant: "vibrant",
    defaultMode: "light",
    entityPalette: [
      "#ED6154", "#F68329", "#F5A931", "#209297", "#032E5A",
      "#105DA9", "#7989C4", "#065752", "#79A999", "#BDD6CD",
      "#EDD7C0", "#CCBCB0", "#F9B0A9", "#DB466A", "#0F1F32",
      "#4D6B7C", "#ACC5D5", "#E1C8AE", "#BCAEA3", "#F3E4D3",
      "#CDCCCB",
    ],
    roleOverrides: {
      light: { primary: "#B84A2E", onPrimary: "#ffffff" },
    },
  },
  {
    id: "dark-academia",
    name: "Dark Academia",
    description: "Candlelit libraries, leather & brass",
    source: "#6b4f3a",
    sourceDark: "#6b4f3a",
    variant: "content",
    defaultMode: "dark",
    entityPalette: [
      "#F5DBB5", "#F0C287", "#D9A86E", "#B87D46", "#876344",
      "#8E431F", "#AB5D1A", "#954015", "#78320B", "#59270A",
      "#3E1C09", "#3E0B06", "#491713", "#400403", "#4B401D",
      "#333218", "#353532", "#171C21", "#30190E", "#1C110A",
      "#090909",
    ],
    roleOverrides: {
      dark: {
        surface: "#1e1813", surfaceContainerLow: "#29211a", surfaceContainer: "#29211a",
        onSurface: "#e8dcc4", primary: "#d4a24a", onPrimary: "#241a10", outlineVariant: "#3a2f24",
      },
    },
  },
  {
    id: "cyberpunk",
    name: "Cyberpunk",
    description: "Neon noir on blue-black",
    source: "#ea00d9",
    sourceDark: "#0abdc6",
    variant: "vibrant",
    defaultMode: "dark",
    entityPalette: [
      "#F92C7C", "#F919C8", "#B80FF1", "#5520EF", "#019EF7",
      "#01D5E4", "#02B9AC", "#24F3B3", "#9AF827", "#F6F20B",
      "#FBA202", "#FA600C", "#EB1D10", "#C1021B", "#A66BF5",
      "#3F4F77", "#626A7C", "#5D626E", "#3B4048", "#202329",
      "#030507",
    ],
    roleOverrides: {
      dark: {
        surface: "#0a0a16", surfaceContainerLow: "#12122a", surfaceContainer: "#141430",
        onSurface: "#d6f7fb", primary: "#0abdc6", onPrimary: "#04141a", outlineVariant: "#2a2a55",
        secondaryContainer: "#1c1c40", onSecondaryContainer: "#d6f7fb",
      },
    },
  },
  {
    id: "art-nouveau",
    name: "Art Nouveau",
    description: "Mucha & stained glass",
    source: "#1f7a7a",
    variant: "content",
    defaultMode: "light",
    entityPalette: [
      "#F0DAB9", "#F0D0A4", "#F7CD7D", "#F5B14E", "#EE903E",
      "#EE8D5C", "#D48071", "#B16A68", "#A78BA0", "#8F89A3",
      "#707A94", "#658285", "#8D9D86", "#89906B", "#857F4C",
      "#797546", "#A88861", "#A57749", "#83542B", "#593E29",
      "#42413D",
    ],
    roleOverrides: {
      light: {
        surface: "#f3eee2", surfaceContainerLow: "#fbf8ef", primary: "#1c7070",
        onPrimary: "#ffffff", outlineVariant: "#e2dac8",
      },
    },
  },
  {
    id: "cottagecore",
    name: "Cottagecore",
    description: "A sun-faded garden",
    source: "#7a8c5a",
    variant: "content",
    defaultMode: "light",
    entityPalette: [
      "#6D7346", "#919669", "#B0AD78", "#C2BC9E", "#CEB796",
      "#E7CDAA", "#F4E3C9", "#C5837F", "#E0A89F", "#E8B39B",
      "#D1886D", "#D9AB67", "#D5B48E", "#A78360", "#657683",
      "#8FA1AB", "#C1C8D2", "#BBB5C2", "#BCAEA0", "#AC998B",
      "#7A6756",
    ],
    roleOverrides: {
      light: {
        surface: "#f6efe1", surfaceContainerLow: "#fffaf0", primary: "#5f7239",
        onPrimary: "#ffffff", outlineVariant: "#e2d8c4",
      },
    },
  },
  {
    id: "baroque",
    name: "Baroque",
    description: "Opulent and candlelit",
    source: "#7a1f2b",
    sourceDark: "#7a1f2b",
    variant: "content",
    defaultMode: "dark",
    entityPalette: [
      "#EBCEA5", "#F0CB95", "#DDAD73", "#D6A060", "#D28939",
      "#EA9A76", "#C56C5C", "#B34F1A", "#A75008", "#BC6A14",
      "#B66716", "#852F0E", "#631209", "#4C090D", "#5F5E3F",
      "#423919", "#1A2519", "#2D343C", "#151D2C", "#302B28",
      "#110D0A",
    ],
    roleOverrides: {
      dark: {
        surface: "#1a0f12", surfaceContainerLow: "#241317", surfaceContainer: "#241317",
        onSurface: "#ecdfc6", primary: "#d4af37", onPrimary: "#1a0f12", outlineVariant: "#3a2228",
      },
    },
  },
  {
    id: "scandinavian",
    name: "Scandinavian",
    description: "Bright, soft and airy",
    source: "#8aa0b5",
    variant: "neutral",
    defaultMode: "light",
    entityPalette: [
      "#F7F7F7", "#F4F0E9", "#E4E3E3", "#CDCCCC", "#C6CCD1",
      "#C6D1DA", "#B1C2CE", "#D6C7B7", "#E3D8CC", "#CFC6BD",
      "#BBB1A7", "#BBBCB4", "#ACB0A6", "#90998D", "#939686",
      "#7E8578", "#727F87", "#506976", "#415C6D", "#4A4A4A",
      "#232321",
    ],
    roleOverrides: {
      light: {
        surface: "#f7f6f3", surfaceContainerLow: "#ffffff", primary: "#587389",
        onPrimary: "#ffffff", outlineVariant: "#dcdad3",
      },
    },
  },
  {
    id: "wabi-sabi",
    name: "Japanese",
    description: "Restrained, natural, wabi-sabi",
    source: "#1f4e8c",
    variant: "content",
    defaultMode: "light",
    entityPalette: [
      "#F4DDBC", "#F0C38D", "#DCA46F", "#F0B09B", "#E78179",
      "#EA865F", "#C62913", "#CD430E", "#850D09", "#622531",
      "#896281", "#20223F", "#3E4B5C", "#3D685B", "#65643B",
      "#4B4821", "#693112", "#BD6919", "#973B17", "#3B3939",
      "#0B0B0B",
    ],
    roleOverrides: {
      light: {
        surface: "#f5f0e8", surfaceContainerLow: "#fbf8f1", onSurface: "#2c2c2c",
        primary: "#1f4e8c", onPrimary: "#ffffff", outlineVariant: "#e0d8ca",
      },
    },
  },
  {
    id: "gothic",
    name: "Gothic",
    description: "Obsidian, blood and candlelight",
    source: "#5b2a6e",
    sourceDark: "#5b2a6e",
    variant: "content",
    defaultMode: "dark",
    entityPalette: [
      "#E0CBAF", "#CAB499", "#CAA884", "#908176", "#726F6C",
      "#454446", "#232A32", "#81464B", "#51394C", "#3B2C4C",
      "#1A273B", "#243545", "#213335", "#293326", "#3F3B25",
      "#312F20", "#423229", "#302119", "#371518", "#270F10",
      "#141415",
    ],
    roleOverrides: {
      dark: {
        surface: "#14121a", surfaceContainerLow: "#1d1a26", surfaceContainer: "#1d1a26",
        onSurface: "#d7cfc0", primary: "#b184d4", onPrimary: "#2a1636", outlineVariant: "#2e2a3a",
      },
    },
  },
  {
    id: "bauhaus",
    name: "Bauhaus",
    description: "Bold geometric primaries",
    source: "#1e3878",
    variant: "vibrant",
    defaultMode: "light",
    entityPalette: [
      "#FDF0D5", "#F6E3C2", "#D8BF9B", "#CEAF83", "#B2A591",
      "#7A766C", "#1F1F1C", "#B91E0F", "#992713", "#0B3576",
      "#1B3947", "#7C8160", "#EDA510", "#CF9133", "#CA4F24",
      "#903013", "#6D7D91", "#3A4D41", "#864B1F", "#38281A",
      "#040402",
    ],
    roleOverrides: {
      light: {
        surface: "#f4f1ea", surfaceContainerLow: "#fffdf7", onSurface: "#1a1a1a",
        primary: "#c8302a", onPrimary: "#ffffff", outlineVariant: "#d9d3c4",
      },
    },
  },
];

export function findPreset(presetId: string): ThemePreset {
  return THEME_PRESETS.find((p) => p.id === presetId) ?? THEME_PRESETS[0]!;
}

function sameHexPalette(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((hex, index) => hex.toLowerCase() === b[index]!.toLowerCase());
}

/** Upgrade saved preset configs whose code-defined defaults changed. Custom
 *  palettes are left alone; this only recognizes known legacy defaults. */
export function normalizePresetConfig(config: ThemeConfig): ThemeConfig {
  const legacyPalette =
    config.preset === "default" ? LEGACY_DEFAULT_ENTITY_PALETTE
      : config.preset === "art-nouveau" ? LEGACY_ART_NOUVEAU_ENTITY_PALETTE
      : config.preset === "baroque" ? LEGACY_BAROQUE_ENTITY_PALETTE
      : config.preset === "dark-academia" ? LEGACY_DARK_ACADEMIA_ENTITY_PALETTE
      : config.preset === "scandinavian" ? LEGACY_SCANDINAVIAN_ENTITY_PALETTE
      : config.preset === "wabi-sabi" ? LEGACY_WABI_SABI_ENTITY_PALETTE
      : config.preset === "gothic" ? LEGACY_GOTHIC_ENTITY_PALETTE
      : config.preset === "bauhaus" ? LEGACY_BAUHAUS_ENTITY_PALETTE
      : config.preset === "cyberpunk" ? LEGACY_CYBERPUNK_ENTITY_PALETTE
      : config.preset === "cottagecore" ? LEGACY_COTTAGECORE_ENTITY_PALETTE
        : null;
  if (!legacyPalette || !sameHexPalette(config.entityPalette, legacyPalette)) return config;
  return {
    ...config,
    entityPalette: [...findPreset(config.preset).entityPalette],
  };
}

/**
 * Build the baseline {@link ThemeConfig} for a preset. Used on first run AND as
 * the source of truth for "revert to default" (baselines live in code, not the
 * synced record). Pass `id`/`created_at` to preserve identity on a revert.
 */
export function presetConfig(
  presetId: string,
  opts: { id?: string; created_at?: string; now?: string } = {},
): ThemeConfig {
  const preset = findPreset(presetId);
  const now = opts.now ?? new Date().toISOString();
  return {
    id: opts.id ?? newId(),
    preset: preset.id,
    variant: preset.variant,
    source: preset.source,
    sourceDark: preset.sourceDark ?? null,
    roleOverrides: {
      light: { ...(preset.roleOverrides?.light ?? {}) },
      dark: { ...(preset.roleOverrides?.dark ?? {}) },
    },
    entityPalette: [...preset.entityPalette],
    paletteOverrides: {},
    entity: {},
    blockTintDiff: DEFAULT_BLOCK_TINT_DIFF,
    blockGradient: true,
    chromaBoost: DEFAULT_CHROMA_BOOST,
    autoColorCalendars: true,
    created_at: opts.created_at ?? now,
    updated_at: now,
    deleted_at: null,
  };
}
